import { useState } from "react"
import { API_URL } from "../../Config/config"
import { useAppDispatch } from "../../redux/store"
import { showError, showSuccess } from "../../redux/counter/counterSlice"
// import { useUploadImageMutation } from "../../services/bClientApi"

function sanitizeFileName(fileName: string) {
    const trimmedName = fileName.trim()
    const extensionMatch = trimmedName.match(/(\.[^./\\]+)$/)
    const extension = extensionMatch ? extensionMatch[1] : ""
    const nameWithoutExtension = extensionMatch ? trimmedName.slice(0, extensionMatch.index) : trimmedName

    const safeBaseName = nameWithoutExtension
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^[-.]+|[-.]+$/g, "")

    const safeName = safeBaseName || "image"
    return `${safeName}${extension}`
}

export default function useUploadImage() {

    const dispatch = useAppDispatch()

    // const [
    //     uploadImage,
    //     {
    //         error,
    //         isLoading
    //     }
    // ] = useUploadImageMutation() 

    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [imageUploadProgress, setImageUploadProgress] = useState('')

    const uploadImageFn = async (imageInfo: { imageToUpload: any, filename: string }) => {

        let uploadedImageUrl = ''
        setIsLoading(true)
        setImageUploadProgress(`Processing...`)
        setError('')

        try {
            // const uploadedImageUrl = await uploadImage(imageInfo).unwrap()
            // return uploadedImageUrl.imageUrl

            // uploadedImageUrl = await uploadImage(imageInfo)
            uploadedImageUrl = await uploadSignedImage(imageInfo)

            dispatch(showSuccess(`Image uploaded successfully`))
        } catch (error) {
            console.log(`Error uploading image: ${error}`)
            setError(`Error uploading image: ${error}`)
            dispatch(showError(`Error uploading image: ${error}`))
        }

        setIsLoading(false)
        setImageUploadProgress(``)
        return uploadedImageUrl
    }

    const uploadImageMultiFn = async (imageInfo: { imageToUpload: any, filename: string }[]) => {
        const uploadedImageUrls = []
        setIsLoading(true)
        setImageUploadProgress(`Processing...`)
        setError('')

        try {
            for (let i = 0; i < imageInfo.length; i++) {
                // const uploadedImageUrl = await uploadImage(imageInfo[i]).unwrap()
                // uploadedImageUrls.push(uploadedImageUrl.imageUrl)
                setImageUploadProgress(`Processing ${i + 1} of ${imageInfo.length}`)
                // const uploadedImageUrl = await uploadImage(imageInfo[i])
                const uploadedImageUrl = await uploadSignedImage(imageInfo[i])
                uploadedImageUrls.push(uploadedImageUrl)
            }
            dispatch(showSuccess(`Images uploaded successfully`))
        } catch (error) {
            console.log(`Error uploading images: ${error}`)
            setError(`Error uploading images: ${error}`)
            dispatch(showError(`Error uploading images: ${error}`))
        }

        setIsLoading(false)
        setImageUploadProgress(``)
        return uploadedImageUrls
    }


    return {
        uploadImageFn,
        uploadImageMultiFn,
        imageUploadProgress,
        error,
        isLoading
    }
}


export async function uploadImage(uploadImageInfo: { imageToUpload: any, filename: string }) {
    const { imageToUpload, filename } = uploadImageInfo
    const safeFileName = sanitizeFileName(filename)
    imageToUpload.lastModifiedDate = new Date()
    imageToUpload.name = safeFileName
    const formData = new FormData()
    formData.append('image', imageToUpload, safeFileName)
    const uploadedImageData = await fetch(`${API_URL}/upload/image-upload`, {
        method: 'POST',
        body: formData,
        cache: 'no-store'
    }).then((res) => {
        return res.json()
    }).catch(err => {
        return {
            error: err
        }
    })
    if (uploadedImageData && uploadedImageData.imageUrl) {
        return uploadedImageData.imageUrl
    }
    if (uploadedImageData && uploadedImageData.error) {
        throw new Error(uploadedImageData.error)
    }
    return ''
}

function getFileExtension(fileName: string, mimeType: string) {
    const extensionFromName = fileName.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase()
    if (extensionFromName) {
        return extensionFromName
    }

    const normalizedMimeType = mimeType?.toLowerCase() || ''
    const mimeToExtension: Record<string, string> = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/svg+xml': 'svg',
        'image/bmp': 'bmp',
        'image/tiff': 'tif',
        'image/heic': 'heic',
        'image/heif': 'heif'
    }

    if (mimeToExtension[normalizedMimeType]) {
        return mimeToExtension[normalizedMimeType]
    }

    const subtype = normalizedMimeType.split('/')[1] || ''
    return subtype.replace(/\+/g, '').replace(/;.*$/, '')
}

export async function uploadSignedImage(uploadImageInfo: { imageToUpload: any, filename: string }) {

    const safeFileName = sanitizeFileName(uploadImageInfo.filename)
    const safeFileNameWithoutExtension = safeFileName.replace(/\.[^/.]+$/, "")
    const fileType = uploadImageInfo.imageToUpload.type
    const fileExtension = getFileExtension(uploadImageInfo.filename, fileType)

    const objectKey = `${safeFileNameWithoutExtension}-${Date.now()}${fileExtension ? '.' + fileExtension : ''}`
    const s3SigningUrl = `${API_URL}/upload/sign-s3?file-name=${encodeURIComponent(objectKey)}&file-type=${encodeURIComponent(fileType)}`

    const signedUrl = await fetch(s3SigningUrl, {
        method: 'GET',
        cache: 'no-store'
    }).then((res) => {
        return res.json()
    }).catch(err => {
        return {
            error: err
        }
    })

    if (signedUrl && signedUrl.signedUrl) {
        const fileUrl = signedUrl.signedUrl.split('?X-Amz')[0]
        // console.log(`signedUrl: ${signedUrl.signedUrl}`)
        let errorUploading = ''
        await fetch(signedUrl.signedUrl, {
            // const signedUrlResponse = await fetch(signedUrl.signedUrl, {
            method: 'PUT',
            body: uploadImageInfo.imageToUpload,
            headers: {
                'Content-Length': uploadImageInfo.imageToUpload.size.toString(),
                'Content-Type': fileType || 'application/octet-stream'
            },
            // cache: 'no-store'
        }).then((res) => {
            return res
        }).catch(err => {
            // errorUploading = err
            errorUploading = `${uploadImageInfo.filename} - ${err}`
            return {
                error: err
            }
        })

        if (errorUploading) {
            throw new Error(`Error uploading file: ${errorUploading}`)
        }

        // check if the file was uploaded
        const checkFile: any = await fetch(fileUrl, {
            method: 'HEAD',
            cache: 'no-store'
        }).then((res) => {
            return res
        }).catch(err => {
            return {
                error: err
            }
        })

        if (checkFile && checkFile.status !== 200) {
            throw new Error(`File not uploaded: ${checkFile.status}`)
        }

        // if (!signedUrlResponse.error || Object.keys(signedUrlResponse.error).length === 0) {
        return fileUrl
        // }

        // if (signedUrlResponse.error) {
        //     throw new Error(signedUrlResponse.error)
        // }
    }

    if (signedUrl && signedUrl.error) {
        // throw new Error(signedUrl.error)
        throw new Error(`Error getting signed url: ${signedUrl.error}`)
    }

    return ''
}